import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'

/**
 * Motion Preferences Context
 *
 * Manages user animation preferences with accessibility in mind.
 * - Respects `prefers-reduced-motion` system setting
 * - Allows manual toggle override
 * - Persists user choice in localStorage
 *
 * CONFIGURATION:
 * - STORAGE_KEY: localStorage key for persistence
 * - Default behavior follows system preference
 */

const STORAGE_KEY = 'motion-preferences'

const MotionPreferencesContext = createContext(null)

export const MotionPreferencesProvider = ({ children }) => {
  // Check system preference
  const getSystemPreference = useCallback(() => {
    if (typeof window === 'undefined') return false
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches
  }, [])

  // Get stored preference or default to motion ON
  const getInitialState = useCallback(() => {
    if (typeof window === 'undefined') return { reducedMotion: false, source: 'default' }

    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored) {
        const parsed = JSON.parse(stored)
        return { reducedMotion: parsed.reducedMotion, source: 'user' }
      }
    } catch (e) {
      console.warn('Failed to read motion preferences:', e)
    }

    // Default: motion is always ON (reducedMotion = false)
    return { reducedMotion: false, source: 'default' }
  }, [])

  const [preferences, setPreferences] = useState(getInitialState)

  // System preference changes are ignored - motion stays ON by default
  // Users can manually toggle it off if needed

  // Toggle motion preference
  const toggleMotion = useCallback(() => {
    setPreferences(prev => {
      const newState = { reducedMotion: !prev.reducedMotion, source: 'user' }
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(newState))
      } catch (e) {
        console.warn('Failed to save motion preferences:', e)
      }
      return newState
    })
  }, [])

  // Reset to system preference
  const resetToSystem = useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEY)
    } catch (e) {
      console.warn('Failed to clear motion preferences:', e)
    }
    setPreferences({ reducedMotion: getSystemPreference(), source: 'system' })
  }, [getSystemPreference])

  // Apply CSS class to document for global CSS-based animations
  useEffect(() => {
    if (preferences.reducedMotion) {
      document.documentElement.classList.add('reduced-motion')
    } else {
      document.documentElement.classList.remove('reduced-motion')
    }
  }, [preferences.reducedMotion])

  const value = {
    reducedMotion: preferences.reducedMotion,
    source: preferences.source,
    toggleMotion,
    resetToSystem,
    // Helper for conditional animation classes
    animationClass: (normalClass, reducedClass = '') =>
      preferences.reducedMotion ? reducedClass : normalClass
  }

  return (
    <MotionPreferencesContext.Provider value={value}>
      {children}
    </MotionPreferencesContext.Provider>
  )
}

export const useMotionPreferences = () => {
  const context = useContext(MotionPreferencesContext)
  if (!context) {
    throw new Error('useMotionPreferences must be used within MotionPreferencesProvider')
  }
  return context
}

export default MotionPreferencesContext
